import { Injectable } from "@angular/core";
import { AIModelChangeService } from "../ai/model-changes/ai-model-change.service";
import { AnalysisEdge, AnalysisNode } from "../common/analysis-model";
import { GraphData, ORGANISATION_MODEL_KEY } from "../common/linqset-model";
import { AIModelChange, checkModelChange } from "../common/model-changes";
import { createInsightsFilter, createProject, Organisation, Project, PROJECT_DESCRIPTION_MAX, PROJECT_NAME_MAX } from "../common/organisation";
import { EpmAppliedChange } from "../epm/epm-future-state";
import { EpmFutureStateService } from "../epm/epm-future-state.service";
import { MODEL_API, ORGANISATION_API, RepositoryService } from "../linq-utils/repository.service";
import { InsightsService } from "../model-services/insights.service";
import { OrganisationService } from "../organisation/organisation.service";
import { compareScenarios, organisationCost, roleCapacitiesFrom, ScenarioComparison, ScenarioMetrics, scenarioMetrics } from "./scenario-comparison";
import { buildSimulationReport, SimulationReport, SimulationSnapshot } from "./simulation-report";
import { SimulationRun } from "./simulation-run";

const COMPONENT = "Simulation scenarios"

// the simulated time each tick stands for, as on the analysis page
export const SCENARIO_SECONDS_PER_TICK = 360
// the working days each scenario is simulated for: long enough for queues to show, short enough to wait for
export const SCENARIO_DAYS = 10

export type ScenarioStep = 'creating' | 'applying' | 'baseline' | 'current' | 'draft'

export interface ScenarioRun {
    snapshot:SimulationSnapshot
    report:SimulationReport
    metrics:ScenarioMetrics
}

export interface ScenarioResult {
    project:Project
    changes:EpmAppliedChange[]
    current:ScenarioRun
    draft:ScenarioRun
    comparison:ScenarioComparison
}

// tries suggested changes in a new project and simulates it against the current state, for the user to keep or delete
@Injectable({ providedIn: 'root' })
export class ScenarioService {
    constructor(private modelChanges:AIModelChangeService, private futureStates:EpmFutureStateService, private organisationService:OrganisationService,
        private insightsService:InsightsService, private repoService:RepositoryService) {}

    // makes the changes in a new project, then simulates the models given in it and in the current state. If it fails
    // before the comparison, the project is removed
    async test(organisation:Organisation, changes:AIModelChange[], modelKeys:string[], name:string,
        progress:(step:ScenarioStep, share?:number) => void, formatCost:(value:number) => string):Promise<ScenarioResult> {
        progress('creating')
        const project = await this.createProject(scenarioName(name), `Testing: ${changes.map(c => c.description).join('; ')}`)
        let baseline:Project|undefined

        try {
            progress('applying')
            const prepared = await this.modelChanges.prepare(organisation, project)
            const rejected:EpmAppliedChange[] = []
            const usable = changes.filter(c => {
                const reason = checkModelChange(c, prepared.context)
                if (reason) rejected.push({ decision: '', description: c.description, applied: false, reason })
                return !reason
            })
            const applied = usable.length ? await this.modelChanges.apply(organisation, project, prepared, usable) : []
            if (!applied.some(c => c.applied)) throw new Error(`None of the changes could be made: ${[...applied, ...rejected].map(c => c.reason).filter(r => r).join('; ') || 'they no longer match the model'}`)
            // the project's insights were worked out on the server
            const latest = await this.organisationService.getOrganisation(organisation._key)
            if (latest?.models) organisation.models = latest.models

            // the current state as a project without the changes, its insights worked out just as the other's were, so
            // the changes are all that differ between them. It's removed once compared
            progress('baseline')
            baseline = await this.createProject(scenarioName('unchanged, for comparison'), 'The current state, to compare a scenario with')
            await this.repoService.callSingleAPI(this.repoService.update(`${ORGANISATION_API(organisation._key, baseline._key)}/refresh`, {}), COMPONENT, "Work out the current state's insights")

            const keys = new Set(modelKeys)
            const current = await this.simulate(organisation, baseline._key, keys, share => progress('current', share))
            const draft = await this.simulate(organisation, project._key, keys, share => progress('draft', share))
            const comparison = compareScenarios(current, draft, formatCost)
            return { project, changes: [...applied, ...rejected], current, draft, comparison }
        }catch(err) {
            await this.discard(project).catch(e => console.error("Unable to remove the scenario's project", e))
            throw err
        }finally {
            if (baseline) await this.discard(baseline).catch(e => console.error("Unable to remove the comparison's current state", e))
        }
    }

    // keeps a what-if as a project: the changes that make it (the load at inputs, the share of items on detours, the
    // people on roles) are made in a new project, which is kept under the name given. If none can be made, nothing is kept
    async keepWhatIf(organisation:Organisation, changes:AIModelChange[], name:string, progress:(step:'creating'|'applying'|'keeping') => void):Promise<{ project:Project, applied:EpmAppliedChange[] }> {
        progress('creating')
        const project = await this.createProject(scenarioName(name), `What if: ${changes.map(c => c.description).join('; ')}`)
        try {
            progress('applying')
            const prepared = await this.modelChanges.prepare(organisation, project)
            const rejected:EpmAppliedChange[] = []
            const usable = changes.filter(c => {
                const reason = checkModelChange(c, prepared.context)
                if (reason) rejected.push({ description: c.description, applied: false, reason })
                return !reason
            })
            const applied = usable.length ? await this.modelChanges.apply(organisation, project, prepared, usable) : []
            if (!applied.some(c => c.applied)) throw new Error(`None of the changes could be made: ${[...applied, ...rejected].map(c => c.reason).filter(r => r).join('; ') || 'they no longer match the model'}`)
            progress('keeping')
            await this.keep(project, name)
            const latest = await this.organisationService.getOrganisation(organisation._key)
            if (latest?.models) organisation.models = latest.models
            return { project, applied: [...applied, ...rejected] }
        }catch(err) {
            await this.discard(project).catch(e => console.error("Unable to remove the what-if's project", e))
            throw err
        }
    }

    private async createProject(name:string, description:string) {
        const project = createProject()
        project.name = name
        project.description = description.slice(0, PROJECT_DESCRIPTION_MAX)
        await this.organisationService.createProject(project)
        return project
    }

    // simulates a workspace's models, as the analysis page shows them, for the scenario's working days
    async simulate(organisation:Organisation, workspaceKey:string, modelKeys:Set<string>, progress:(share:number) => void) {
        const [data, organisationModel] = await Promise.all([
            this.insightsService.loadAnalysisGraphForFilter(organisation, createInsightsFilter(workspaceKey)) as Promise<{ nodes:AnalysisNode[], edges:AnalysisEdge[] }>,
            this.repoService.callSingleAPI(this.repoService.getData<GraphData>(MODEL_API(organisation._key, ORGANISATION_MODEL_KEY, workspaceKey)), COMPONENT, "Load the organisation model"),
        ])
        const run = SimulationRun.fromAnalysis(data, modelKeys, SCENARIO_SECONDS_PER_TICK)
        await run.runFor(SCENARIO_DAYS, progress)
        const snapshot = run.snapshot()
        const report = buildSimulationReport(snapshot, roleCapacitiesFrom(organisationModel, workspaceKey), new Map(organisation.models.map(m => [m._key, m.name])))
        return { snapshot, report, metrics: scenarioMetrics(snapshot, report, organisationCost(organisationModel, workspaceKey)) }
    }

    discard(project:Project) {
        return this.futureStates.discard(project)
    }

    // names the scenario's project as the user chooses
    keep(project:Project, name:string) {
        return this.futureStates.rename(project, name)
    }
}

// a name for a scenario's project, until the user gives it their own
const scenarioName = (name:string) => {
    const full = `Scenario: ${name}`
    return full.length > PROJECT_NAME_MAX ? full.slice(0, PROJECT_NAME_MAX - 1).trimEnd() + '…' : full
}
